/**
 * Hook to load Google Maps API script
 * 
 * Usage:
 * const { isLoaded, loadError } = useGoogleMaps(apiKey);
 * 
 * if (loadError) return <div>Error loading maps</div>;
 * if (!isLoaded) return <div>Loading maps...</div>;
 * 
 * // Now you can use google.maps
 */

import { useEffect, useState } from 'react';

interface UseGoogleMapsOptions {
  apiKey: string;
  libraries?: string[];
  version?: string;
}

export function useGoogleMaps({ apiKey, libraries = [], version = 'weekly' }: UseGoogleMapsOptions) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google?.maps) {
      setIsLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      // Wait for it to load
      existingScript.addEventListener('load', () => setIsLoaded(true));
      existingScript.addEventListener('error', () => setLoadError(new Error('Failed to load Google Maps')));
      return;
    }

    // Create script element
    const script = document.createElement('script');
    const librariesParam = libraries.length > 0 ? `&libraries=${libraries.join(',')}` : '';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}${librariesParam}&v=${version}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setIsLoaded(true);
    };

    script.onerror = () => {
      setLoadError(new Error('Failed to load Google Maps API'));
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts before loading
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [apiKey, libraries.join(','), version]);

  return { isLoaded, loadError };
}

